import { useEffect, useState } from "react";
import { referrals } from "@/lib/api";
import { Gift, Copy, Check, MousePointerClick, UserPlus, ShoppingBag } from "lucide-react";

const ShareStat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-zinc-200 p-4 text-center">
    <Icon className="w-4 h-4 text-[#4F46E5] mx-auto mb-2" strokeWidth={1.5} />
    <div className="font-display font-black text-2xl tracking-tight">{value}</div>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mt-1">{label}</div>
  </div>
);

const ReferralPanel = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referrals.me().then(setData).catch(() => {});
  }, []);

  if (!data) return null;

  const copy = () => {
    navigator.clipboard.writeText(data.link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareText = encodeURIComponent(
    "Aprende con Kursos Online — 40 cursos en 10 nichos. Únete con mi enlace:"
  );
  const encoded = encodeURIComponent(data.link);
  const shares = [
    { name: "WhatsApp", url: `https://wa.me/?text=${shareText}%20${encoded}`, testid: "referral-share-whatsapp" },
    { name: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`, testid: "referral-share-facebook" },
    { name: "X", url: `https://twitter.com/intent/tweet?text=${shareText}&url=${encoded}`, testid: "referral-share-x" },
    { name: "Telegram", url: `https://t.me/share/url?url=${encoded}&text=${shareText}`, testid: "referral-share-telegram" },
  ];

  return (
    <section data-testid="referral-panel" className="mt-16">
      <div className="flex items-center gap-2 mb-2">
        <Gift className="w-4 h-4 text-[#10B981]" />
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981]">Invita y crece</p>
      </div>
      <h2 className="font-display font-bold text-2xl mb-6">Tu enlace de referidos</h2>

      <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-emerald-950 text-white p-6 md:p-8">
        <p className="text-sm text-zinc-300 max-w-lg mb-5">
          Comparte tu enlace único. Cuando alguien entra por él y se registra, suma a tu comunidad.
          Es la forma más orgánica de hacer crecer Kursos Online.
        </p>

        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <input
            readOnly
            value={data.link}
            data-testid="referral-link-input"
            className="flex-1 bg-slate-800/70 border border-slate-700 px-4 py-3 text-sm font-mono outline-none"
          />
          <button
            onClick={copy}
            data-testid="referral-copy-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#059669] px-5 py-3 font-semibold text-sm transition-colors"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copiado" : "Copiar"}
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {shares.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={s.testid}
              className="text-xs font-semibold border border-slate-600 hover:border-[#4F46E5] hover:bg-[#4F46E5]/20 px-4 py-2 transition-colors"
            >
              {s.name}
            </a>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4">
        <ShareStat icon={MousePointerClick} label="Clics" value={data.clicks} />
        <ShareStat icon={UserPlus} label="Registros" value={data.signups} />
        <ShareStat icon={ShoppingBag} label="Compradores" value={data.buyers} />
      </div>
    </section>
  );
};

export default ReferralPanel;
